import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { hostname } from "../utils";

interface SmartList {
  id: string;
  name: string;
  type?: string;
  count?: number;
}

interface SmartListPickerProps {
  companyId: string;
  getToken: (() => Promise<string>) | (() => string) | Function;
  value: string;
  onChange: (listId: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  required?: boolean;
  emptyLabel?: string;
  showCount?: boolean;
  renderItem?: (list: SmartList) => React.ReactNode;
}

const SmartListPicker: React.FC<SmartListPickerProps> = ({
  companyId,
  getToken,
  value,
  onChange,
  label = "Smart List",
  helperText,
  disabled = false,
  size = "medium",
  fullWidth = true,
  required = false,
  emptyLabel = "None",
  showCount = false,
  renderItem,
}) => {
  const [lists, setLists] = useState<SmartList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLists = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/lists`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId, minimal: !showCount },
        });
        setLists(response.data.items || []);
      } catch (err) {
        console.error("Failed to load lists:", err);
        setError("Failed to load lists");
        setLists([]);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      loadLists();
    }
  }, [companyId, getToken, showCount]);

  const isDisabled = disabled || loading || (lists.length === 0 && !error);

  const defaultRenderItem = (list: SmartList) => {
    if (showCount && list.count !== undefined) {
      return `${list.name} (${list.count} records)`;
    }
    return list.name;
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} disabled={isDisabled}>
      <InputLabel required={required}>
        {label}
        {loading && (
          <CircularProgress size={12} sx={{ ml: 1 }} />
        )}
      </InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
      >
        <MenuItem value="">
          <em>
            {loading
              ? "Loading..."
              : error
              ? error
              : lists.length === 0
              ? "No lists available"
              : emptyLabel}
          </em>
        </MenuItem>
        {lists.map((list) => (
          <MenuItem key={list.id} value={list.id}>
            {renderItem ? renderItem(list) : defaultRenderItem(list)}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default SmartListPicker;
